import { forkJoin } from 'rxjs';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { PartnerSelectorComponent } from '../partner/partner-selector.component';
import { DocumentTypeEntity } from '../distribution/models/distribution-object';
import { DistributionObjectSelectorComponent } from '../distribution/distribution-object-selector.component';
import { DocumentConfigSelectorSimpleComponent } from '../distribution/document-config-selector-simple.component';

import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { StateService } from '../shared/state-management/state.service';
import { Direction, DistributionObjectGroup } from './models';
import { Guid } from '../shared/utils';
import { DistributionObjectSelectorComponent as DistributionObjectSelectorComponent_1 } from './distribution-object-selector.component';
import { FormsModule } from '@angular/forms';
import { DocumentConfigSelectorSimpleComponent as DocumentConfigSelectorSimpleComponent_1 } from './document-config-selector-simple.component';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';

@Component({
    templateUrl: './distribution-search.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DistributionObjectSelectorComponent_1, FormsModule, PartnerSelectorComponent, DocumentConfigSelectorSimpleComponent_1, SvgIconComponent]
})
export class DistributionSearchComponent implements OnInit {

    constructor(private router: Router, private loadingPanelService: LoadingPanelService, private stateService: StateService) { }

    private readonly destroyRef = inject(DestroyRef);

    public searchObject: any = { splitFromAndTo: false };
    public isLoadingFromState = false;
    public isSplitted = false;
    readonly stateKey: string = 'distribution-search-state';

    @ViewChild('docConfigInSimple', { static: true }) private docConfigIn: DocumentConfigSelectorSimpleComponent;
    @ViewChild('docConfigOutSimple', { static: true }) private docConfigOut: DocumentConfigSelectorSimpleComponent;

    @ViewChild('partnerSelectorFrom', { static: true }) private partnerSelectorFrom: PartnerSelectorComponent;
    @ViewChild('partnerSelectorTo', { static: true }) private partnerSelectorTo: PartnerSelectorComponent;

    @ViewChild('distributionObjectSelector', { static: true }) private distributionObjectSelector: DistributionObjectSelectorComponent;

    ngOnInit(): void {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Initialize data...'));

        forkJoin([
            this.distributionObjectSelector.initialized,
            this.docConfigIn.initialized,
            this.docConfigOut.initialized
        ]).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.loadingPanelService.hideLoadingPanel();
            if (!this.setStateData()) {
                this.initializeSearchObject();
            }
        });
    }

    splitFromAndToChange($event) {
        this.docConfigIn.direction = $event.target?.checked ? Direction.In : null;
        this.docConfigIn.refresh();
    }

    setStateData(): boolean {
        const stateData = this.stateService.getState(this.stateKey);
        if (stateData) {
            this.isLoadingFromState = true;

            this.initializeSearchObject();
            // To keep the "default" values from the reset-function - merge the two search-objects.
            Object.assign(this.searchObject, stateData.searchObject);

            if (this.searchObject.partnerFromId) {
                this.partnerSelectorFrom.setSelectedPartnerId((this.searchObject.partnerFromId));
            }
            if (this.searchObject.partnerToId) {
                this.partnerSelectorTo.setSelectedPartnerId(this.searchObject.partnerToId);
            }

            let docInPromise: Promise<void>;
            let docOutPromise: Promise<void>;

            if (this.searchObject.distributionObjectGroupId) {
                this.distributionObjectSelector.setSelectedDistributionObjectGroupId(this.searchObject.distributionObjectGroupId);
            }

            if (this.searchObject.distributionObjectId) {
                this.distributionObjectSelector.setSelectedDistributionObjectId(this.searchObject.distributionObjectId);
                docInPromise = this.docConfigIn.refresh(this.searchObject.distributionObjectId);
                docOutPromise = this.docConfigOut.refresh(this.searchObject.distributionObjectId);
            }

            if (this.searchObject.documentConfigIdFrom) {
                const executor = () => this.docConfigIn.setSelected(this.searchObject.documentConfigIdFrom);
                if (docInPromise != null) {
                    docInPromise.then(executor);
                }
                else {
                    executor();
                }
            }
            if (this.searchObject.documentConfigIdTo) {
                const executor = () => this.docConfigOut.setSelected(this.searchObject.documentConfigIdTo);
                if (docOutPromise != null) {
                    docOutPromise.then(executor);
                }
                else {
                    executor();
                }
            }

            this.isLoadingFromState = false;
            return true;
        }

        return false;
    }

    distributionObjectChanged(distributionObject: DocumentTypeEntity) {
        if (!this.isLoadingFromState) {
            this.docConfigIn.refresh(distributionObject ? distributionObject.Id : null);
            this.docConfigOut.refresh(distributionObject ? distributionObject.Id : null);
            this.searchObject.distributionObjectId = distributionObject ? distributionObject.Id : null;
        }
    }

    distributionObjectGroupChanged(distributionObjectGroup: DistributionObjectGroup) {
        if (distributionObjectGroup && distributionObjectGroup.DistributionObjectGroupId) {
            this.searchObject.distributionObjectGroupId = distributionObjectGroup.DistributionObjectGroupId;
        }
        else {
            this.searchObject.distributionObjectGroupId = null;
        }
    }


    search() {
        // Clean the search-object to prevent ugly unnecessary null-values in url-parameters
        const tempSearchObject: any = {};
        for (const searchProperty in this.searchObject) {
            if (this.searchObject[searchProperty] === false || this.searchObject[searchProperty]) { // Include booleans with false value and all other properties with a value
                tempSearchObject[searchProperty] = this.searchObject[searchProperty];
            }
        }
        tempSearchObject.searchId = Guid.newGuid();

        const stateData: any = {};
        stateData.searchObject = tempSearchObject;

        this.stateService.setState(this.stateKey, stateData);

        this.router.navigate(['distribution/result', tempSearchObject]);
    }

    createNew() {
        this.router.navigate(['distribution/result/details', { createNew: true }]);
    }

    reset(resetState: boolean = true) {
        if (resetState) {
            this.isLoadingFromState = false;
            this.stateService.removeState(this.stateKey);
        }

        this.isSplitted = false;
        this.initializeSearchObject();

        this.docConfigIn.direction = null;
        this.docConfigIn.reset();
        this.docConfigOut.reset();

        this.partnerSelectorFrom.reset();
        this.partnerSelectorTo.reset();

        this.distributionObjectSelector.reset();
    }

    initializeSearchObject() {
        this.searchObject = { splitFromAndTo: false };
        this.searchObject.environmentSpecificSearch = true;
    }
}
